<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from './PosUiIcon.vue';
  import { chipAriaAttributes } from './pos-ui-aria.utils';
  // `active` defaults to undefined (not Vue's Boolean false) so a one-shot chip
  // that never passes it is not announced as a toggle.
  const props = withDefaults(
    defineProps<{
      active?: boolean;
      count?: number;
      disabled?: boolean;
      icon?: string;
      /** Inside a `role="radiogroup"`: `role="radio"` + `aria-checked` instead of `aria-pressed`. */
      radio?: boolean;
    }>(),
    { active: undefined, count: undefined, icon: undefined, radio: false }
  );
  const emit = defineEmits<{ (e: 'click', event: MouseEvent): void }>();

  const ariaAttributes = computed(() =>
    chipAriaAttributes(props.active, props.radio)
  );
</script>

<template>
  <button
    type="button"
    class="pos-ui-chip"
    :class="{ 'is-active': active }"
    v-bind="ariaAttributes"
    :disabled="disabled"
    @click="emit('click', $event)"
  >
    <PosUiIcon v-if="icon" :name="icon" class="chip-icon" />
    <slot />
    <span v-if="count != null" class="chip-count">{{ count }}</span>
  </button>
</template>

<style scoped lang="scss">
  .pos-ui-chip {
    @apply tw-inline-flex tw-min-h-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-neutral-100 tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-600 tw-transition-colors hover:tw-bg-neutral-200;
    &.is-active {
      @apply tw-bg-neutral-900 tw-text-on-inverse hover:tw-bg-neutral-900;
    }
    &:disabled {
      @apply tw-cursor-not-allowed tw-bg-neutral-100 tw-text-neutral-400 hover:tw-bg-neutral-100;
    }
    &:focus-visible {
      @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
    }
    // Margins, not flex gap: old POS hardware (Chrome < 84).
    .chip-icon {
      @apply tw-mr-1.5;
    }
    .chip-count {
      @apply tw-ml-1.5 tw-font-semibold tw-tabular-nums;
    }
  }
</style>
